import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { smeService } from '../services/api';
import {
  SME_TYPES,
  TYPE_DISPLAY_NAMES,
  getCategoriesForType,
  getSubcategoriesForCategory,
  getCategoryDisplayName,
  getSubCategoryDisplayName,
} from '../constants/smeCategories';
import {
  ArrowLeft,
  User,
  LayoutGrid,
  Folder,
  Award,
  FileText,
  Sparkles,
  Info,
  Save,
  ChevronDown,
  Check
} from 'lucide-react';
import './SMEForm.css';

const getDomainIconColor = (domainName) => {
  const map = {
    'Business': { bg: '#ffedd5', color: '#ea580c', letter: 'B' },
    'Technology': { bg: '#f3e8ff', color: '#9333ea', letter: 'T' },
    'Law': { bg: '#e0f2fe', color: '#0284c7', letter: 'L' },
    'Finance': { bg: '#dcfce7', color: '#16a34a', letter: 'F' },
    'Economics': { bg: '#fef3c7', color: '#d97706', letter: 'E' },
    'Marketing': { bg: '#ffe4e6', color: '#e11d48', letter: 'M' },
    'Science': { bg: '#dbeafe', color: '#2563eb', letter: 'S' },
    'Healthcare': { bg: '#fee2e2', color: '#dc2626', letter: 'H' },
    'Politics': { bg: '#f3f4f6', color: '#4b5563', letter: 'P' },
    'Education': { bg: '#fef9c3', color: '#ca8a04', letter: 'E' },
    'Media': { bg: '#e0e7ff', color: '#4f46e5', letter: 'M' },
    'Engineering': { bg: '#f1f5f9', color: '#475569', letter: 'E' },
    'ArtsAndHumanities': { bg: '#fae8ff', color: '#c026d3', letter: 'A' },
    'Environment': { bg: '#d1fae5', color: '#059669', letter: 'E' },
    'SportsAndWellness': { bg: '#ffedd5', color: '#ea580c', letter: 'S' },
  };
  return map[domainName] || { bg: '#f1f5f9', color: '#475569', letter: domainName ? domainName[0] : 'D' };
};

// Custom Select Component for Domain specifically (with colored letters)
const DomainSelect = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectedOption = options.find(o => o.value === value);

  return (
    <div className="smeform-custom-select" ref={containerRef}>
      <div 
        className={`smeform-select-trigger ${isOpen ? 'open' : ''} ${value ? 'has-value' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="smeform-select-trigger-content">
          <LayoutGrid size={18} strokeWidth={2} className="smeform-field-icon" />
          <span className="smeform-select-value">
            {selectedOption ? selectedOption.label : 'Select a knowledge domain'}
          </span>
        </div>
        <ChevronDown size={18} className="smeform-chevron" />
      </div>

      {isOpen && (
        <div className="smeform-options-dropdown">
          {options.map((opt) => {
            const colors = getDomainIconColor(opt.value);
            return (
              <div 
                key={opt.value}
                className={`smeform-option ${value === opt.value ? 'selected' : ''}`}
                onClick={() => {
                  onChange({ target: { name: 'type', value: opt.value } });
                  setIsOpen(false);
                }}
              >
                <div className="smeform-option-icon" style={{ background: colors.bg, color: colors.color }}>
                  {colors.letter}
                </div>
                <span>{opt.label}</span>
                {value === opt.value && <Check size={16} className="smeform-check" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StandardSelect = ({ icon: Icon, name, value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectedOption = options.find(o => o.value === value);

  return (
    <div className={`smeform-custom-select ${disabled ? 'disabled' : ''}`} ref={containerRef}>
      <div 
        className={`smeform-select-trigger ${isOpen ? 'open' : ''} ${value ? 'has-value' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="smeform-select-trigger-content">
          <Icon size={18} strokeWidth={2} className="smeform-field-icon" />
          <span className="smeform-select-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown size={18} className="smeform-chevron" />
      </div>

      {isOpen && (
        <div className="smeform-options-dropdown">
          {options.length === 0 ? (
            <div className="smeform-option no-options">No options available</div>
          ) : (
            options.map((opt) => (
              <div 
                key={opt.value}
                className={`smeform-option standard ${value === opt.value ? 'selected' : ''}`}
                onClick={() => {
                  onChange({ target: { name, value: opt.value } });
                  setIsOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {value === opt.value && <Check size={16} className="smeform-check" />}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};


const SMEForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    smeCategory: '',
    subCategory: '',
    prompt: '',
    description: '',
    useWebSearch: true,
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSME = async () => {
      try {
        setFetching(true);
        const response = await smeService.getById(id);
        setFormData({
          name: response.data.name || '',
          type: response.data.type || '',
          smeCategory: response.data.smeCategory || '',
          subCategory: response.data.subCategory || '',
          prompt: response.data.prompt || '',
          description: response.data.description || '',
          useWebSearch: response.data.useWebSearch !== false,
        });
        setError(null);
      } catch (err) {
        setError('Failed to load SME details');
        console.error('Error fetching SME:', err);
      } finally {
        setFetching(false);
      }
    };

    if (isEdit) {
      fetchSME();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const resolvedValue = type === 'checkbox' ? checked : value;
    if (name === 'type') {
      const firstCat = getCategoriesForType(value)[0] || '';
      const firstSub = getSubcategoriesForCategory(firstCat)[0] || '';
      setFormData(prev => ({ ...prev, type: value, smeCategory: firstCat, subCategory: firstSub }));
    } else if (name === 'smeCategory') {
      const firstSub = getSubcategoriesForCategory(value)[0] || '';
      setFormData(prev => ({ ...prev, smeCategory: value, subCategory: firstSub }));
    } else {
      setFormData(prev => ({ ...prev, [name]: resolvedValue }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.name || !formData.type || !formData.prompt) {
      setError('Name, type, and prompt are required fields');
      setLoading(false);
      return;
    }

    try {
      if (isEdit) {
        await smeService.update(id, formData);
      } else {
        await smeService.create(formData);
      }
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save SME');
      console.error('Error saving SME:', err);
    } finally {
      setLoading(false);
    }
  };

  if (id && fetching) {
    return (
      <div className="app-loading-container">
        <div className="app-loading-spinner" />
        <p>Loading SME details...</p>
      </div>
    );
  }

  const domainOptions = SME_TYPES.map(t => ({ value: t, label: TYPE_DISPLAY_NAMES[t] }));
  const categoryOptions = getCategoriesForType(formData.type).map(c => ({ value: c, label: getCategoryDisplayName(c) }));
  const subCategoryOptions = getSubcategoriesForCategory(formData.smeCategory).map(c => ({ value: c, label: getSubCategoryDisplayName(c) }));

  return (
    <div className="smeform-page">
      <div className="smeform-main">
        {/* Top Button */}
        <div className="smeform-top-actions">
          <button type="button" className="smeform-btn-back" onClick={() => navigate('/')}>
            <ArrowLeft size={16} strokeWidth={2} />
            Back to List
          </button>
        </div>

        {/* Form Container */}
        <div className="smeform-card">
          <div className="smeform-header-section">
            <div className="smeform-avatar-icon">
              <User size={24} strokeWidth={2} />
            </div>
            <div className="smeform-header-text">
              <h1>{isEdit ? 'Edit SME' : 'Create New SME'}</h1>
              <p>{isEdit ? 'Update the details for this AI subject matter expert.' : 'Add a new AI subject matter expert with domain knowledge and expertise.'}</p>
            </div>
          </div>

          {error && <div className="smeform-error">{error}</div>}

          <form onSubmit={handleSubmit} className="smeform-form">
            
            <div className="smeform-row">
              {/* Name */}
              <div className="smeform-group">
                <label>Name <span className="req">*</span></label>
                <div className="smeform-input-wrapper">
                  <User size={18} className="smeform-field-icon" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter SME name"
                    className="smeform-input"
                    required
                  />
                </div>
                <p className="smeform-hint">Enter a unique and descriptive name for the SME.</p>
              </div>

              {/* Domain */}
              <div className="smeform-group">
                <label>Domain <span className="req">*</span></label>
                <DomainSelect 
                  value={formData.type} 
                  onChange={handleChange} 
                  options={domainOptions} 
                />
              </div>
            </div>

            <div className="smeform-row">
              {/* Sub-Domain */}
              <div className="smeform-group">
                <label>Sub-Domain <span className="req">*</span></label>
                <StandardSelect 
                  icon={Folder}
                  name="smeCategory"
                  value={formData.smeCategory}
                  onChange={handleChange}
                  options={categoryOptions}
                  placeholder="Select a sub-domain"
                  disabled={!formData.type}
                />
                <p className="smeform-hint">Narrow field of knowledge within the domain.</p>
              </div>

              {/* Specialisation */}
              <div className="smeform-group">
                <label>Specialisation</label>
                <StandardSelect 
                  icon={Award}
                  name="subCategory"
                  value={formData.subCategory}
                  onChange={handleChange}
                  options={subCategoryOptions}
                  placeholder="Select a specialisation"
                  disabled={!formData.smeCategory}
                />
                <p className="smeform-hint">Fine-grained area of expertise within the sub-domain.</p>
              </div>
            </div>

            {/* Description */}
            <div className="smeform-group">
              <label>Description</label>
              <div className="smeform-textarea-wrapper">
                <FileText size={18} className="smeform-field-icon top-icon" />
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description (optional)"
                  className="smeform-textarea"
                  maxLength={500}
                />
                <div className="smeform-char-count">{formData.description.length}/500</div>
              </div>
              <p className="smeform-hint">Briefly describe the SME's expertise, experience, and focus areas.</p>
            </div>

            {/* Checkbox */}
            <div className="smeform-group">
              <label className="smeform-checkbox-label">
                <div className="smeform-checkbox-wrap">
                  <input
                    type="checkbox"
                    name="useWebSearch"
                    checked={Boolean(formData.useWebSearch)}
                    onChange={handleChange}
                  />
                  <div className="smeform-checkbox-custom">
                    {formData.useWebSearch && <Check size={12} strokeWidth={3} />}
                  </div>
                </div>
                <span>Enable optional web search for this SME persona</span>
                <Info size={14} className="smeform-info-icon" />
              </label>
            </div>

            {/* Prompt */}
            <div className="smeform-group">
              <label>AI Prompt (Character Definition) <span className="req">*</span></label>
              <div className="smeform-textarea-wrapper">
                <Sparkles size={18} className="smeform-field-icon top-icon" />
                <textarea
                  name="prompt"
                  value={formData.prompt}
                  onChange={handleChange}
                  placeholder="Enter the prompt that defines the AI character for this SME"
                  className="smeform-textarea"
                  required
                  rows={4}
                />
              </div>
              <p className="smeform-hint">This prompt will be used to define the AI's character and behavior when acting as this SME.</p>
            </div>

            {/* Actions */}
            <div className="smeform-footer-actions">
              <button
                type="button"
                className="smeform-btn-cancel"
                onClick={() => navigate('/')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="smeform-btn-submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEdit ? (
                  <>
                    <Save size={16} strokeWidth={2} />
                    Update SME
                  </>
                ) : (
                  <>
                    <Save size={16} strokeWidth={2} />
                    Create SME
                  </>
                ))}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default SMEForm;
